<template>
  <div
    ref="navbar"
    class="app-navbar"
  >
    <nav
      :data-keyboard-area="EKeyboardArea.AppNavigation"
      @keydown="moveFocusOnArrowKey"
    >
      <PrmButton
        size="small"
        text
        severity="primary"
        type="button"
        icon="pi pi-arrow-left"
        :disabled="disabled"
        data-qa="app-navbar__back"
        @click="historyBack"
      />
      <SeparatorAppNavbar />
      <slot />
    </nav>
  </div>
</template>

<script lang="ts" setup>
import PrmButton from '@NitrexGroup/primevue/button';
import {
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
} from 'vue';

import {
  EKeyboardKey,
} from '../../constants/keyboard.ts';
import {
  EKeyboardArea,
} from '../../utils/keyboard-area.ts';
import {
  doesElementUseArrowKeys,
} from '../../utils/keyboard-target.ts';
import {
  rafCallback,
} from '../../utils/raf';

import SeparatorAppNavbar from './separator-app-navbar.vue';

// The entries of the navigation bar are links wrapping a button, which counts as one entry.
const ENTRY_SELECTOR = 'a[href], button:not(:disabled)';

defineProps<{
  disabled?: boolean,
}>();

const navbar = useTemplateRef<HTMLElement>('navbar');

function historyBack() {
  window.history.back();
}

function isNestedEntry(entry: Element): boolean {
  return entry.parentElement?.closest(ENTRY_SELECTOR) != null;
}

function findOutermostEntry(element: Element): HTMLElement | undefined {
  let entry = element.closest<HTMLElement>(ENTRY_SELECTOR) ?? undefined;
  while (entry && isNestedEntry(entry)) {
    entry = entry.parentElement?.closest<HTMLElement>(ENTRY_SELECTOR) ?? undefined;
  }
  return entry;
}

function moveFocusOnArrowKey(event: KeyboardEvent) {
  const isVerticalArrowKey = event.key === EKeyboardKey.ArrowDown || event.key === EKeyboardKey.ArrowUp;
  const hasModifier = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
  if (!isVerticalArrowKey || hasModifier || event.defaultPrevented || !navbar.value || !(event.target instanceof Element)) {
    return;
  }
  if (doesElementUseArrowKeys(event.target)) {
    return;
  }
  const entryList = Array.from(navbar.value.querySelectorAll<HTMLElement>(ENTRY_SELECTOR))
    .filter((entry) => !isNestedEntry(entry) && entry.checkVisibility());
  const focusedEntry = findOutermostEntry(event.target);
  const focusedIndex = focusedEntry
    ? entryList.indexOf(focusedEntry)
    : -1;
  const nextIndex = event.key === EKeyboardKey.ArrowDown
    ? focusedIndex + 1
    : focusedIndex - 1;
  const nextEntry = entryList[nextIndex];
  if (!nextEntry) {
    return;
  }
  event.preventDefault();
  nextEntry.focus();
}

const onResize = rafCallback(() => {
  if (!navbar.value) {
    return;
  }
  document.documentElement.style.setProperty('--navbar-sidebar-width', `${navbar.value!.offsetWidth}px`);
});
onMounted(() => {
  onResize();
  window.addEventListener('resize', onResize);
  navbar.value!.addEventListener('resize', onResize);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  navbar.value!.removeEventListener('resize', onResize);
});
</script>

<style scoped lang="scss">
.app-navbar {
  @media print {
    width: 0;
    overflow: none;
  }

  nav {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: .5rem;
    align-items: stretch;
    padding: .5rem;
    border-right: 1px solid var(--border-color-frame);
    overflow-y: auto;
    background-color: var(--surface-frame);
  }
}
</style>
